import React, { useEffect, useState } from "react";
import api from "../../constants/api";
import { useAuth } from "../ui/AuthContext.jsx";
import { isPowerUser } from "../../constants/roles.js";
import { showError, showSuccess } from "../../utils/toast.js";

const TITLE_BY_KIND = {
  kunde: "Kundendokumente (SharePoint)",
  objekt: "Objektdokumente (SharePoint)",
  service: "Auftragsdokumente (SharePoint)",
  projekt: "Auftragsdokumente (SharePoint)",
  mangel: "Auftragsdokumente (SharePoint)",
  offerte: "Auftragsdokumente (SharePoint)",
};

function folderDepth(byId, folder) {
  let depth = 0;
  let cur = folder;
  const seen = new Set();
  while (cur?.parent_folder_id && byId[cur.parent_folder_id] && depth < 8) {
    if (seen.has(cur.id)) break;
    seen.add(cur.id);
    depth += 1;
    cur = byId[cur.parent_folder_id];
  }
  return depth;
}

/**
 * Lightweight DMS tree panel (Wave 5). Feature + runtime gated.
 */
export default function DmsTreePanel({
  entityKind,
  entityId,
  projektTyp = "",
  className = "",
}) {
  const { sharepointReady, hasFeature, user } = useAuth();
  const power = isPowerUser(user);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [parentId, setParentId] = useState("");

  const enabled =
    sharepointReady && hasFeature("sharepoint_storage") && entityKind && entityId;

  const title = TITLE_BY_KIND[entityKind] || "Dokumente (DMS)";

  async function load() {
    if (!enabled) return;
    setLoading(true);
    try {
      const q = projektTyp ? `?projektTyp=${encodeURIComponent(projektTyp)}` : "";
      const { data } = await api.get(`/dms/${entityKind}/${entityId}/tree${q}`, {
        silent: true,
      });
      setFolders(Array.isArray(data?.folders) ? data.folders : []);
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      if (!parentId && data?.folders?.[0]?.id) {
        setParentId(String(data.folders[0].id));
      }
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || "DMS-Tree konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKind, entityId, projektTyp, enabled]);

  async function createFolder() {
    if (!power || !newName.trim() || !parentId) return;
    try {
      await api.post(`/dms/${entityKind}/${entityId}/folders`, {
        name: newName.trim(),
        parentFolderId: Number(parentId),
        projektTyp: projektTyp || undefined,
      });
      setNewName("");
      showSuccess("Ordner angelegt (Sync pending).");
      await load();
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || "Ordner anlegen fehlgeschlagen.");
    }
  }

  if (!enabled) return null;

  const statusLabel = (s) => {
    if (s === "synced") return "Synchronisiert";
    if (s === "pending" || s === "syncing") return "Wird synchronisiert";
    if (s === "unmapped") return "Von SharePoint";
    if (s === "discovered") return "Von SharePoint";
    if (s === "error" || s === "conflict" || s === "orphaned") return "Gestört";
    return s || "—";
  };

  const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
  const sortedFolders = [...folders].sort((a, b) => {
    const da = folderDepth(byId, a);
    const db = folderDepth(byId, b);
    if (da !== db) return da - db;
    return String(a.display_name || "").localeCompare(String(b.display_name || ""), "de");
  });

  return (
    <div className={`rounded border border-slate-200 bg-white p-3 ${className}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {loading ? <span className="text-xs text-slate-500">Laden…</span> : null}
      </div>

      <ul className="mb-3 max-h-72 space-y-1 overflow-auto text-sm">
        {sortedFolders.map((f) => (
          <li
            key={`f-${f.id}`}
            className="flex items-center justify-between gap-2"
            style={{ paddingLeft: `${folderDepth(byId, f) * 12}px` }}
          >
            <span className="truncate">📁 {f.display_name}</span>
            {power ? (
              <span className="shrink-0 text-xs text-slate-500">{statusLabel(f.sync_status)}</span>
            ) : null}
          </li>
        ))}
        {documents.map((d) => {
          const parent = d.folder_id ? byId[d.folder_id] : null;
          const depth = parent ? folderDepth(byId, parent) + 1 : 0;
          return (
            <li
              key={`d-${d.id}`}
              className="flex items-center justify-between gap-2"
              style={{ paddingLeft: `${depth * 12}px` }}
            >
              <span className="truncate">📄 {d.display_name || d.original_filename || d.id}</span>
              {power ? (
                <span className="shrink-0 text-xs text-slate-500">{statusLabel(d.sync_status)}</span>
              ) : null}
            </li>
          );
        })}
        {!folders.length && !documents.length && !loading ? (
          <li className="text-xs text-slate-500">Noch keine DMS-Einträge.</li>
        ) : null}
      </ul>

      {power ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-2">
          <label className="text-xs text-slate-600">
            Parent
            <select
              className="ml-1 rounded border px-1 py-0.5 text-xs"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">—</option>
              {sortedFolders.map((f) => (
                <option key={f.id} value={f.id}>
                  {"—".repeat(folderDepth(byId, f))} {f.display_name}
                </option>
              ))}
            </select>
          </label>
          <input
            className="min-w-[8rem] flex-1 rounded border px-2 py-1 text-xs"
            placeholder="Neuer Ordner"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            type="button"
            className="rounded bg-slate-800 px-2 py-1 text-xs text-white disabled:opacity-50"
            disabled={!newName.trim() || !parentId}
            onClick={createFolder}
          >
            + Ordner
          </button>
        </div>
      ) : null}
    </div>
  );
}
